import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, X, ChevronDown, ChevronUp, MessageSquare, ArrowRight, HelpCircle } from 'lucide-react';
import Header from '@/components/public/Header';
import Footer from '@/components/public/Footer';
import { FAQPageSEO } from '@/components/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BASE_URL = 'https://starteaseai.com';

// Category configuration
const CATEGORIES = [
  { value: 'all', label: 'All Questions', icon: '📋' },
  { value: 'investment', label: 'Investment Documents', icon: '💼' },
  { value: 'ai', label: 'AI Documents', icon: '📑' },
  { value: 'formation', label: 'Business Formation', icon: '🏢' },
  { value: 'compliance', label: 'Compliance', icon: '✅' },
  { value: 'state', label: 'State Selection', icon: '🗺️' },
  { value: 'banking', label: 'Banking', icon: '🏦' },
  { value: 'tax', label: 'Tax', icon: '📊' },
  { value: 'payment', label: 'Payment', icon: '💳' },
  { value: 'documents', label: 'Documents', icon: '📄' },
  { value: 'general', label: 'General', icon: '❓' },
];

// FAQ Accordion Item
const FAQItem = ({ faq, isOpen, onToggle, index }) => {
  return (
    <div 
      className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-emerald-300 transition-colors"
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 text-left flex justify-between items-start gap-4 hover:bg-slate-50 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="font-medium text-slate-900 leading-relaxed pr-4">
          {faq.question}
        </span>
        <span className={`flex-shrink-0 mt-0.5 transition-transform ${isOpen ? 'text-emerald-600' : 'text-slate-400'}`}>
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>
      
      {isOpen && (
        <div className="px-6 pb-5 border-t border-slate-100">
          <div 
            className="pt-4 text-slate-600 leading-relaxed prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: faq.answer_html || `<p>${faq.answer}</p>` }}
          />
          {faq.related_service_slug && (
            <Link 
              to={faq.related_service_slug.startsWith('/') ? faq.related_service_slug : `/usa/${faq.related_service_slug}`}
              className="inline-flex items-center gap-1 mt-4 text-emerald-600 text-sm font-medium hover:text-emerald-700"
            >
              Learn more about this service <ArrowRight size={14} />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * FAQPage - Comprehensive FAQ page with search, category filtering
 * Matches the site aesthetic with dark hero, emerald accents
 */
const FAQPage = () => {
  const [allFaqs, setAllFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState(new Set());

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/public/faq-items`, {
          params: { limit: 500 }
        });
        setAllFaqs(response.data.items || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching FAQs:', err);
        setError(err.response?.data?.detail || 'Failed to load FAQs');
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  // Get category counts
  const categoryCounts = useMemo(() => {
    const counts = { all: allFaqs.length };
    allFaqs.forEach(faq => {
      if (faq.category) {
        counts[faq.category] = (counts[faq.category] || 0) + 1;
      }
    });
    return counts;
  }, [allFaqs]);

  // Filter FAQs by category and search query
  const filteredFaqs = useMemo(() => {
    let result = allFaqs;
    
    if (selectedCategory !== 'all') {
      result = result.filter(faq => faq.category === selectedCategory);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(faq => 
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [allFaqs, selectedCategory, searchQuery]);

  const toggleItem = (index) => {
    setOpenItems(prev => {
      const newSet = new Set(prev);
      if (prev.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setOpenItems(new Set(filteredFaqs.map((_, i) => i)));
  };

  const collapseAll = () => {
    setOpenItems(new Set());
  };

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <FAQPageSEO 
        pageTitle="Frequently Asked Questions — US Company Formation & Investment Docs | StartEase Agent"
        pageDescription="Answers to common questions about US LLC formation, C-Corp, EIN, registered agent, BOI report, and investment document drafting for non-US founders."
        canonicalUrl="https://starteaseai.com/faq"
        faqItems={filteredFaqs}
        keywords="FAQ, LLC formation questions, business formation help, compliance FAQ, EIN questions"
      />
      
      <Header />

      {/* Hero Section */}
      <section className="bg-[#1e293b] py-16 md:py-20" data-testid="faq-hero">
        <div className="container-custom text-center">
          <span className="text-emerald-400 font-semibold text-sm uppercase tracking-widest mb-4 block">
            Help Center
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto mb-8">
            Find answers to common questions about US business formation, investment documents, AI contracts, banking, and more.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search questions..."
              className="w-full pl-12 pr-12 py-4 bg-white/10 backdrop-blur border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="faq-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                data-testid="faq-search-clear"
              >
                <X size={20} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Category Filter - Sticky */}
      <section className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10" data-testid="faq-category-filter">
        <div className="container-custom">
          <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                  selectedCategory === cat.value
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
                data-testid={`faq-category-${cat.value}`}
              >
                <span>{cat.icon}</span>
                {cat.label}
                {categoryCounts[cat.value] > 0 && (
                  <span className={`text-xs ${selectedCategory === cat.value ? 'text-emerald-200' : 'text-slate-400'}`}>
                    ({categoryCounts[cat.value] || 0})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12 md:py-16" data-testid="faq-content">
        <div className="container-custom max-w-3xl">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500">Loading FAQs...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-500 mb-4">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              {/* Results Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="text-slate-500 text-sm">
                  {searchQuery ? (
                    <span>
                      Found <strong className="text-slate-800">{filteredFaqs.length}</strong> results for "{searchQuery}"
                    </span>
                  ) : (
                    <span>
                      Showing <strong className="text-slate-800">{filteredFaqs.length}</strong> questions
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={expandAll}
                    className="text-xs text-slate-500 hover:text-emerald-600 transition-colors"
                  >
                    Expand all
                  </button>
                  <span className="text-slate-300">|</span>
                  <button
                    onClick={collapseAll}
                    className="text-xs text-slate-500 hover:text-emerald-600 transition-colors"
                  >
                    Collapse all
                  </button>
                </div>
              </div>

              {/* FAQ List */}
              {filteredFaqs.length > 0 ? (
                <div className="space-y-3">
                  {filteredFaqs.map((faq, index) => (
                    <FAQItem
                      key={faq.id || index}
                      faq={faq}
                      index={index}
                      isOpen={openItems.has(index)}
                      onToggle={() => toggleItem(index)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-slate-50 rounded-xl">
                  <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No questions found</h3>
                  <p className="text-slate-500 mb-6">
                    {searchQuery 
                      ? `No results for "${searchQuery}". Try a different search term.`
                      : 'No FAQs in this category yet.'}
                  </p>
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                    }}
                    className="text-emerald-600 font-medium hover:text-emerald-700"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-slate-50 py-16" data-testid="faq-contact-cta">
        <div className="container-custom max-w-2xl text-center">
          <MessageSquare className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Still have questions?
          </h2>
          <p className="text-slate-600 mb-8">
            Can't find what you're looking for? Our team is here to help with any questions about US business formation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors rounded-lg"
            >
              Contact Support
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link 
              to="/login"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 font-medium hover:bg-slate-100 transition-colors rounded-lg border border-slate-200"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
